// CoverageStatusBadge — the COVERAGE status of a leave request (derived by
// leave-service: not_required | unassigned | pending | covered |
// needs_attention), shown to the employee and, prominently, to the approver.
// Separate from the leave status and the delegation status — never conflated.
// An absent / unknown status renders nothing: it is never shown as "covered".
import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle2, Clock, ShieldOff, UserX } from 'lucide-react';
import { coverageReasonText, coverageStatusMeta } from './handover';

const TONE: Record<string, string> = {
  success: 'bg-success-soft text-success-strong',
  warning: 'bg-warning-soft text-warning-strong',
  danger: 'bg-danger-soft text-danger-strong',
  muted: 'bg-muted text-muted-foreground',
};
const ICON: Record<string, React.ComponentType<{ className?: string }>> = {
  covered: CheckCircle2,
  pending: Clock,
  unassigned: UserX,
  needs_attention: AlertTriangle,
  not_required: ShieldOff,
};

export interface CoverageStatusBadgeProps {
  status?: string | null;
  reason?: string | null;
  delegateName?: string | null;
  /** Show the reason sentence under the badge (approver view). */
  withReason?: boolean;
  className?: string;
}

export const CoverageStatusBadge: React.FC<CoverageStatusBadgeProps> = ({ status, reason, delegateName, withReason, className }) => {
  const { t } = useTranslation(['timeoff']);
  const meta = coverageStatusMeta(status);
  if (!meta || !status) return null;
  const Icon = ICON[status] ?? Clock;
  const why = withReason ? coverageReasonText(reason, delegateName) : '';
  return (
    <div className={className} data-testid="coverage-status">
      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${TONE[meta.tone]}`}>
        <Icon className="h-3.5 w-3.5" />
        {t(`timeoff:coverage_state_${status}`, { defaultValue: meta.label })}
        {status === 'pending' && delegateName ? ` · ${delegateName}` : ''}
        {status === 'covered' && delegateName ? ` · ${delegateName}` : ''}
      </span>
      {why && <p className={`mt-1 text-xs ${status === 'needs_attention' ? 'text-danger-strong' : 'text-muted-foreground'}`}>{why}</p>}
    </div>
  );
};

/** Ask before approving a request whose cover isn't confirmed. Never a block:
 *  approving anyway is allowed and recorded server-side. Resolves true to go
 *  ahead. Without a ConfirmProvider (standalone render) it simply goes ahead. */
export async function confirmApproveAnyway(
  confirm: ((o: { title?: string; message: React.ReactNode; confirmLabel?: string; cancelLabel?: string }) => Promise<boolean>) | null,
  t: (k: string, o?: Record<string, unknown>) => string,
  item: { status?: string | null; reason?: string | null; delegateName?: string | null; name?: string },
): Promise<boolean> {
  if (!confirm || item.status !== 'needs_attention') return true;
  return confirm({
    title: t('timeoff:approve_anyway_title', { defaultValue: 'Cover needs attention' }),
    message: `${coverageReasonText(item.reason, item.delegateName) || t('timeoff:approve_anyway_generic', { defaultValue: "This time off doesn't have confirmed cover." })} ${t('timeoff:approve_anyway_note', { defaultValue: 'You can still approve it — that will be recorded on the request.' })}`,
    confirmLabel: t('timeoff:approve_anyway', { defaultValue: 'Approve anyway' }),
    cancelLabel: t('timeoff:approve_anyway_cancel', { defaultValue: 'Not yet' }),
  });
}

export default CoverageStatusBadge;
